import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { Locale } from '../../lang/index';
import { ApiService } from '../../services/index';
import { CustomViewExportSnapshot } from '../../types/snapshot.types';
import { useAppMutation } from '../use-app-mutation.hook';
import { useLocale } from '../use-locale.hook';

const POLL_INTERVAL_MS = 3_000;
const MAX_POLL_ATTEMPTS = 200;

type CapTableExportJobStatus = 'failed' | 'pending' | 'processing' | 'ready';

type CapTableExportJobResponse = {
  downloadUrl?: string;
  errorMessage?: string;
  fileName?: string;
  jobId: string;
  status: CapTableExportJobStatus;
};

type StartExportResponse = {
  jobId: string;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const downloadFromUrl = (url: string, fileName: string) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

const pollExportJob = async (companyId: string, jobId: string, locale: Locale) => {
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
    const status = await ApiService.get<CapTableExportJobResponse>(
      {
        endpoint: BackendRoute.COMPANIES,
        routePath: [companyId, 'cap-table-export-jobs', jobId],
      },
      { locale },
    );

    if (status.status === 'ready' && status.downloadUrl) {
      return status;
    }

    if (status.status === 'failed') {
      throw new HttpException(new Response(null, { status: 500 }), {
        message: status.errorMessage || 'Export failed',
      });
    }

    await sleep(POLL_INTERVAL_MS);
  }

  throw new HttpException(new Response(null, { status: 408 }), {
    message: 'Export timed out. Please try again.',
  });
};

export const useExport = ({
  companyId,
  snapshotId = '',
}: {
  companyId: string;
  snapshotId?: string;
}) => {
  const { locale } = useLocale();
  const exportLatest = [companyId, 'snapshots', 'export-current', 'async'];
  const exportCurrent = [companyId, 'snapshots', snapshotId, 'export'];

  const { isPending, mutate: mutateExport } = useAppMutation(
    [QueryKey.EXPORT_SNAPSHOT, QueryKey.CAP_TABLE_EXPORT_JOB, companyId],
    {
      mutationFn: async (body: CustomViewExportSnapshot) => {
        if (snapshotId) {
          await ApiService.downloadFile(
            {
              endpoint: BackendRoute.COMPANIES,
              routePath: exportCurrent,
            },
            { body, locale },
          );
          return;
        }

        const { jobId } = await ApiService.post<StartExportResponse, CustomViewExportSnapshot>(
          {
            endpoint: BackendRoute.COMPANIES,
            routePath: exportLatest,
          },
          { body, locale },
        );

        const result = await pollExportJob(companyId, jobId, locale);
        downloadFromUrl(result.downloadUrl!, result.fileName || 'CapTable.xlsx');
      },
    },
  );

  return { isPending, mutateExport };
};
